import {
  useCallback,
} from 'react';
import type {
  ReactElement,
} from 'react';
import {
  Switch,
  Route,
  Redirect,
} from 'react-router-dom';

import { NestedListLayout } from '../NestedListLayout';
import { NestedShowLayout } from '../NestedShowLayout';

import { TabRoute } from './TabRoute';
import type {
  NestedRouteLayoutPropsWithCurrentTab,
  NestedRoutesLayoutProps,
} from './types';

export function NestedRoutesLayout<Payload = undefined>({
  breadcrumbs,
  title,
  actionsBlock = undefined,
  headerBlock = undefined,
  tabs,
  status = undefined,
  statusColor = undefined,
  payload = (undefined as Payload),
  redirectFrom,
  redirectTo,
}: NestedRoutesLayoutProps<Payload>): ReactElement {
  const Layout = useCallback(({
    currentTab,
    breadcrumbs: nestedBreadcrumbs,
    title: nestedTitle,
    actionsBlock: nestedActionsBlock,
    children,
  }: NestedRouteLayoutPropsWithCurrentTab): ReactElement => {
    const mergedBreadcrumbs = nestedBreadcrumbs
      ? [...breadcrumbs, ...nestedBreadcrumbs]
      : breadcrumbs;

    if (nestedTitle || nestedActionsBlock) {
      return (
        <NestedListLayout
          breadcrumbs={mergedBreadcrumbs}
          parentTitle={title}
          parentActionsBlock={actionsBlock}
          headerBlock={headerBlock}
          tabs={tabs}
          currentTab={currentTab}
          parentStatus={status}
          parentStatusColor={statusColor}
          title={nestedTitle}
          actionsBlock={nestedActionsBlock}
        >
          {children}
        </NestedListLayout>
      );
    }

    return (
      <NestedShowLayout
        breadcrumbs={mergedBreadcrumbs}
        title={title}
        actionsBlock={actionsBlock}
        headerBlock={headerBlock}
        tabs={tabs}
        currentTab={currentTab}
        status={status}
        statusColor={statusColor}
      >
        {children}
      </NestedShowLayout>
    );
  }, [
    breadcrumbs,
    title,
    actionsBlock,
    headerBlock,
    tabs,
    status,
    statusColor,
  ]);

  return (
    <Switch>
      {
        tabs.map(({
          id,
          path,
          to,
          component: TabComponent,
        }) => (
          <Route
            path={path || to}
            render={(routeProps) => (
              <TabRoute
                currentTab={id}
                routeProps={routeProps}
                payload={payload}
                Layout={Layout}
                TabComponent={TabComponent}
              />
            )}
            key={id}
          />
        ))
      }

      {
        redirectFrom && redirectTo && (
          <Redirect
            from={redirectFrom}
            to={redirectTo}
          />
        )
      }
    </Switch>
  );
}
